import classNames from "classnames";
import { Entry, EntrySkeletonType, UnresolvedLink } from "contentful";

import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import {
  assertEditorialType,
  isActiveAsset,
  isActiveEntry,
} from "~/utils/editorial-utils";

import styles from "./breakpoint-aware-asset.module.scss";
import MediaAsset from "./media-asset";

type Props = {
  entry: UnresolvedLink<"Entry"> | Entry<EntrySkeletonType>;
  className?: string;
  fill?: boolean;
  width?: number;
  height?: number;
  videoConfig?: {
    muted?: boolean;
    loop?: boolean;
    autoPlay?: boolean;
    controls?: boolean;
    playsInline?: boolean;
  };
  noLazy?: boolean;
};

const defaultVideoConfig = Constants.DEFAULT_VIDEO_CONFIG;

export default function BreakpointAwareAsset({
  entry,
  className,
  videoConfig,
  ...rest
}: Props) {
  if (!entry || !isActiveEntry(entry)) return null;

  assertEditorialType<Editorials.ContentAwareAsset>(
    entry,
    "breakpointAwareAsset",
  );

  const {
    posterMobile,
    posterDesktop,
    autoPlay,
    controls,
    loop,
    muted,
    playsInline,
  } = entry.fields;
  const videoConfiguration = {
    autoPlay: autoPlay ?? videoConfig?.autoPlay ?? defaultVideoConfig.autoPlay,
    controls: controls ?? videoConfig?.controls ?? defaultVideoConfig.controls,
    loop: loop ?? videoConfig?.loop ?? defaultVideoConfig.loop,
    muted: muted ?? videoConfig?.muted ?? defaultVideoConfig.muted,
    playsInline:
      playsInline ?? videoConfig?.playsInline ?? defaultVideoConfig.playsInline,
  };

  return (
    <>
      <MediaAsset
        entry={entry.fields.mobileAsset}
        poster={
          isActiveAsset(posterMobile)
            ? posterMobile.fields?.file?.url
            : undefined
        }
        className={classNames(styles.mobileAsset, className)}
        {...rest}
        sizes="(min-width: 900px) 0vw, 100vw"
        videoConfig={videoConfiguration}
      />
      <MediaAsset
        entry={entry.fields.desktopAsset}
        poster={
          isActiveAsset(posterDesktop)
            ? posterDesktop.fields?.file?.url
            : undefined
        }
        className={classNames(styles.desktopAsset, className)}
        {...rest}
        sizes="(min-width: 900px) 100vw, 0vw"
        videoConfig={videoConfiguration}
      />
    </>
  );
}
